/**
 * @file klaviyo-back-in-stock-mask.js
 * @description Tags the email input inside Klaviyo's back-in-stock modal with data-cs-mask
 * for Contentsquare PII masking. Klaviyo renders the modal inside its own
 * <iframe id="klaviyo-bis-iframe" src="about:blank">, populated by Klaviyo's onsite.js after
 * the iframe loads — the input never appears in the main document, so it has to be tagged
 * inside the iframe's own document once that content is written.
 */

const BIS_IFRAME_SELECTOR = 'iframe#klaviyo-bis-iframe';
const EMAIL_INPUT_SELECTOR = 'input[type="email"]';

/**
 * @param {Document} doc
 * @returns {boolean} whether at least one email input was found (and masked)
 */
function maskEmailInputs(doc) {
  const inputs = doc.querySelectorAll(EMAIL_INPUT_SELECTOR);
  inputs.forEach((input) => input.setAttribute('data-cs-mask', ''));
  return inputs.length > 0;
}

/**
 * about:blank iframes written to by same-origin script inherit that origin, so
 * contentDocument is reachable here — contentDocument returns null (rather than
 * throwing) if Klaviyo ever changes the iframe to a cross-origin src, so the
 * !doc?.body check below is enough to degrade silently.
 * @param {HTMLIFrameElement} iframe
 */
function watchBisIframe(iframe) {
  let innerObserver = null;

  const tryMask = () => {
    const doc = iframe.contentDocument;
    if (!doc?.body) return;

    // Each reopen re-fires 'load' (Klaviyo re-writes the iframe document) — drop
    // any observer from the previous open before starting a new one.
    innerObserver?.disconnect();

    if (maskEmailInputs(doc.body)) return;

    innerObserver = new MutationObserver(() => {
      if (maskEmailInputs(doc.body)) innerObserver.disconnect();
    });
    innerObserver.observe(doc.body, { childList: true, subtree: true });
  };

  iframe.addEventListener('load', tryMask);
  tryMask();
}

/**
 * Starts observing the document for the Klaviyo back-in-stock iframe and, once it
 * appears, tags its email input as soon as Klaviyo writes the modal content.
 * @returns {MutationObserver}
 */
export function initKlaviyoBackInStockMask() {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        const iframe = node.matches(BIS_IFRAME_SELECTOR) ? node : node.querySelector?.(BIS_IFRAME_SELECTOR);
        if (!iframe) continue;

        // The iframe is created once and reused for every reopen (its own 'load'
        // listener handles those) — no need to keep scanning the whole page for it.
        watchBisIframe(iframe);
        observer.disconnect();
        return;
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
  return observer;
}

initKlaviyoBackInStockMask();
